import { MouseEvent, ReactNode } from "react";

import Tooltip from "@components/tooltip/tooltip";
import { classNames } from "@helpers/css";
import { titleCase } from "@helpers/string";

export type ToggleButtonGroupType<T> = {
  tooltip?: string;
  className?: string;
  title: string | ReactNode;
  active: boolean;
  disabled?: boolean;
  value?: T;
};

export enum ToggleButtonGroupTheme {
  roundedCompact,
  mergedLightBlueButtons,
  separatedWhiteButtons,
  sidebar,
  defaultButtons,
}

export const ToggleButtonGroup = <T,>({
  buttons,
  onClick,
  className = "",
  theme,
}: {
  className?: string;
  onClick: (
    button: ToggleButtonGroupType<T>,
    e: MouseEvent<HTMLButtonElement>
  ) => void;
  buttons: ToggleButtonGroupType<T>[];
  theme: ToggleButtonGroupTheme;
}) => {
  const handleClick =
    (button: ToggleButtonGroupType<T>) =>
    (e: MouseEvent<HTMLButtonElement>) => {
      onClick(button, e);
    };

  return (
    <div
      className={classNames(
        "flex flex-wrap shrink-0",
        theme === ToggleButtonGroupTheme.roundedCompact &&
          "gap-0.5 p-0.5 rounded-full bg-black/10",
        theme === ToggleButtonGroupTheme.mergedLightBlueButtons &&
          "rounded bg-white border divide-x",
        theme === ToggleButtonGroupTheme.sidebar && "gap-4",
        theme === ToggleButtonGroupTheme.separatedWhiteButtons && "gap-[10px]",
        theme === ToggleButtonGroupTheme.defaultButtons &&
          "gap-0 bg-gray-100 rounded-md h-[30px] ring-1 ring-gray-200",
        className
      )}
    >
      {buttons.map((button) => (
        <Tooltip key={String(button.value)} text={button.tooltip}>
          <button
            type="button"
            className={classNames(
              "shrink-0 flex items-center",
              theme === ToggleButtonGroupTheme.roundedCompact &&
                "px-1.5 py-px tracking-tight rounded-full text-xs font-medium",
              theme === ToggleButtonGroupTheme.roundedCompact
                ? button.active
                  ? "bg-gray-50 text-gray-800"
                  : "hover:text-gray-800 text-gray-600"
                : null,
              theme === ToggleButtonGroupTheme.mergedLightBlueButtons &&
                "px-3 h-[30px] text-sm text-gray-600",
              theme === ToggleButtonGroupTheme.mergedLightBlueButtons
                ? button.active
                  ? "bg-blue-50 text-blue-800"
                  : "hover:text-gray-800 hover:bg-gray-50"
                : null,
              theme === ToggleButtonGroupTheme.sidebar &&
                "text-sm font-semibold px-1",
              theme === ToggleButtonGroupTheme.sidebar
                ? button.active
                  ? "text-gray-700"
                  : "text-gray-400 hover:text-gray-800"
                : null,
              theme === ToggleButtonGroupTheme.separatedWhiteButtons &&
                "text-gray-900 text-sm font-medium px-2 py-1 border rounded-md",
              theme === ToggleButtonGroupTheme.separatedWhiteButtons
                ? button.active
                  ? "bg-gray-100"
                  : "hover:bg-gray-50"
                : null,
              theme === ToggleButtonGroupTheme.defaultButtons &&
                "px-4 rounded-md h-[30px]",
              theme === ToggleButtonGroupTheme.defaultButtons
                ? button.active
                  ? "bg-white text-gray-800 ring-1 ring-gray-200"
                  : "text-gray-500 hover:text-gray-800"
                : null,
              button.className
            )}
            disabled={button.disabled}
            onClick={handleClick(button)}
          >
            {typeof button.title === "string"
              ? titleCase(button.title)
              : button.title}
          </button>
        </Tooltip>
      ))}
    </div>
  );
};
